import Link from "next/link";
import { Box, Chip, IconButton, Paper, Stack, Typography } from "@mui/material";
import { ArrowUpRight, Check, Film, Sparkles, Trash2 } from "lucide-react";

import type { Clip } from "@/features/clips/types";
import type { Moment } from "@/features/moments/types";
import { RelativeTime } from "@/features/sources/relative-time";
import type { Source, SourcePipelineStatus } from "@/features/sources/types";
import { apiBaseUrl } from "@/lib/api-client";

import { StatusBadge, type UiStatus } from "./status-badge";
import { PipelineProgress } from "./pipeline-progress";
import { formatClock, pluralize } from "@/lib/format";

export function sourceUiStatus(
  source: Source,
  pipeline: SourcePipelineStatus | null,
  moments: Moment[],
  clips: Clip[]
): UiStatus {
  if (source.status === "failed" || pipeline?.has_error) return "failed";
  if (pipeline?.has_active_work || source.status === "queued" || source.status === "downloading") return "processing";
  const activeMoments = moments.filter((moment) => moment.status !== "superseded");
  const allApproved = activeMoments.length > 0 && activeMoments.every((moment) =>
    clips.some((clip) => clip.moment_id === moment.id && clip.review_status === "approved")
  );
  if (allApproved) return "approved";
  if (clips.some((clip) => clip.status === "rendered")) return "needs_review";
  return "ready";
}

export function SourceCard({
  source,
  pipeline,
  moments,
  clips,
  onDelete
}: {
  source: Source;
  pipeline: SourcePipelineStatus | null;
  moments: Moment[];
  clips: Clip[];
  onDelete?: (source: Source) => void;
}) {
  const status = sourceUiStatus(source, pipeline, moments, clips);
  const activeMoments = moments.filter((moment) => moment.status !== "superseded");
  const renderedCount = clips.filter((clip) => clip.status === "rendered" && clip.render_preset !== "square_1_1").length;
  const approvedCount = clips.filter((clip) => clip.review_status === "approved").length;

  return (
    <Paper
      component={Link}
      href={`/sources/${source.id}`}
      variant="outlined"
      sx={{
        display: "block",
        color: "text.primary",
        textDecoration: "none",
        overflow: "hidden",
        transition: "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "rgba(139,124,246,.42)",
          boxShadow: "0 24px 60px rgba(0,0,0,.20)"
        }
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 9", bgcolor: "#151923", overflow: "hidden" }}>
        {source.thumbnail_url ? (
          <Box
            component="img"
            src={`${apiBaseUrl}${source.thumbnail_url}`}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block", transition: "transform 300ms ease", ".MuiPaper-root:hover &": { transform: "scale(1.025)" } }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center", background: "radial-gradient(circle, rgba(139,124,246,.18), transparent 60%)" }}>
            <Film size={34} opacity={0.55} />
          </Box>
        )}
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(7,9,14,.82), transparent 58%)" }} />
        <Box sx={{ position: "absolute", top: 12, left: 12 }}><StatusBadge status={status} /></Box>
        {onDelete ? (
          <IconButton
            size="small"
            onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDelete(source); }}
            sx={{ position: "absolute", right: 8, top: 8, color: "white", bgcolor: "rgba(7,9,14,.5)", "&:hover": { bgcolor: "rgba(220,38,38,.85)" } }}
          >
            <Trash2 size={15} />
          </IconButton>
        ) : (
          <ArrowUpRight size={19} style={{ position: "absolute", right: 14, top: 14, color: "white" }} />
        )}
        <Typography variant="caption" sx={{ position: "absolute", bottom: 11, right: 13, color: "rgba(255,255,255,.82)", fontWeight: 750 }}>
          {formatDuration(source.duration_seconds)}
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ p: 2 }}>
        <Stack spacing={0.5}>
          <Typography fontWeight={800} sx={{ minHeight: 48, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {source.title ?? source.original_url}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Added <RelativeTime value={source.created_at} />
          </Typography>
        </Stack>

        {status === "processing" && pipeline ? <PipelineProgress pipeline={pipeline} /> : null}

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" icon={<Sparkles size={13} />} label={pluralize(activeMoments.length, "idea")} />
          <Chip size="small" variant="outlined" icon={<Film size={13} />} label={pluralize(renderedCount, "clip")} />
          <Chip size="small" variant="outlined" icon={<Check size={13} />} label={`${approvedCount} approved`} />
        </Stack>
      </Stack>
    </Paper>
  );
}

function formatDuration(duration: number | null) {
  return duration ? formatClock(duration) : "—";
}
